import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  CSSProperties,
} from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

export const INTERACTIVE_CONSTANTS = {
  BUTTON: {
    SIZE: {
      HEIGHT: 9,
      WIDTH: 9,
    },
    ICON: {
      SIZE: 18,
    },
    SPACING: {
      RIGHT: 4,
      BOTTOM: 4,
      BETWEEN: 2.8,
    },
  },
  THEME: {
    LIGHT: {
      BACKGROUND: '#b8b8b8',
      HOVER_BACKGROUND: '#E5E7EB',
      ICON_COLOR: '#303030',
      BORDER_COLOR: '#242424',
    },
    DARK: {
      BACKGROUND: '#00000033',
      HOVER_BACKGROUND: '#000000',
      ICON_COLOR: '#FFFFFF',
      BORDER_COLOR: '#00000000',
    },
  },
  ANIMATION: {
    DURATION: 300,
    SCALE: 0.95,
  },
  CONTAINER: {
    BORDER_RADIUS: 10,
    MARGIN_BOTTOM: 8,
  },
} as const;

type CustomButtonProps = {
  onClick: () => void;
  theme: 'light' | 'dark';
  children: React.ReactNode;
};

export const CustomButton: React.FC<CustomButtonProps> = ({
  onClick,
  theme,
  children,
}) => {
  const themeConstants =
    INTERACTIVE_CONSTANTS.THEME[
      theme.toUpperCase() as keyof typeof INTERACTIVE_CONSTANTS.THEME
    ];

  return (
    <button
      onClick={onClick}
      className="absolute items-center justify-center rounded-xl hover:border hover:border-gray-800 hover:shadow-sm"
      style={{
        height: INTERACTIVE_CONSTANTS.BUTTON.SIZE.HEIGHT * 4,
        width: INTERACTIVE_CONSTANTS.BUTTON.SIZE.WIDTH * 4,
        right: INTERACTIVE_CONSTANTS.BUTTON.SPACING.RIGHT * 4,
        bottom: INTERACTIVE_CONSTANTS.BUTTON.SPACING.BOTTOM * 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 12,
        backgroundColor: themeConstants.BACKGROUND,
        border: 'none',
        cursor: 'pointer',
        transition: `all ${INTERACTIVE_CONSTANTS.ANIMATION.DURATION}ms`,
      }}
      onMouseEnter={(e) => {
        const target = e.currentTarget;
        target.style.backgroundColor = themeConstants.HOVER_BACKGROUND;
        target.style.transform = `scale(${INTERACTIVE_CONSTANTS.ANIMATION.SCALE})`;
      }}
      onMouseLeave={(e) => {
        const target = e.currentTarget;
        target.style.backgroundColor = themeConstants.BACKGROUND;
        target.style.transform = 'scale(1)';
      }}
    >
      {children}
    </button>
  );
};

export type InteractiveForwardRefProps = {
  play: () => void;
  reset: () => void;
  pause: () => void;
};

type ButtonType = 'play' | 'reset' | 'pause' | 'play-pause';

type ButtonConfig = {
  type: ButtonType;
  onClick: () => void;
  isPlaying?: boolean;
};

type InteractiveButtonsProps = {
  buttons: ButtonConfig[];
  theme: 'light' | 'dark';
};

export const InteractiveButtons: React.FC<InteractiveButtonsProps> = ({
  buttons,
  theme,
}) => {
  const themeConstants =
    INTERACTIVE_CONSTANTS.THEME[
      theme.toUpperCase() as keyof typeof INTERACTIVE_CONSTANTS.THEME
    ];
  const iconColor = themeConstants.ICON_COLOR;

  const getButtonIcon = (type: ButtonType, isPlaying?: boolean) => {
    switch (type) {
      case 'reset':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill={iconColor}
            width={INTERACTIVE_CONSTANTS.BUTTON.ICON.SIZE}
            height={INTERACTIVE_CONSTANTS.BUTTON.ICON.SIZE}
          >
            <path
              fill={iconColor}
              d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"
            />
          </svg>
        );
      case 'play-pause':
        return isPlaying ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill={iconColor}
            width={INTERACTIVE_CONSTANTS.BUTTON.ICON.SIZE}
            height={INTERACTIVE_CONSTANTS.BUTTON.ICON.SIZE}
          >
            <path
              d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"
              stroke={iconColor}
              fill={iconColor}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill={iconColor}
            width={INTERACTIVE_CONSTANTS.BUTTON.ICON.SIZE}
            height={INTERACTIVE_CONSTANTS.BUTTON.ICON.SIZE}
          >
            <path
              d="M8 5.14v14.72L19 12 8 5.14z"
              stroke={iconColor}
              fill={iconColor}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case 'pause':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill={iconColor}
            width={INTERACTIVE_CONSTANTS.BUTTON.ICON.SIZE}
            height={INTERACTIVE_CONSTANTS.BUTTON.ICON.SIZE}
          >
            <path
              d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"
              stroke={iconColor}
              fill={iconColor}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case 'play':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill={iconColor}
            width={INTERACTIVE_CONSTANTS.BUTTON.ICON.SIZE}
            height={INTERACTIVE_CONSTANTS.BUTTON.ICON.SIZE}
          >
            <path
              d="M8 5.14v14.72L19 12 8 5.14z"
              stroke={iconColor}
              fill={iconColor}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
    }
  };

  return (
    <>
      {buttons.map((button, index) => (
        <View
          key={button.type}
          className="absolute bottom-0"
          style={
            {
              right: INTERACTIVE_CONSTANTS.BUTTON.SPACING.BETWEEN * index * 16,
            } as ViewStyle
          }
        >
          <CustomButton theme={theme} onClick={button.onClick}>
            {getButtonIcon(button.type, button.isPlaying)}
          </CustomButton>
        </View>
      ))}
    </>
  );
};

type InteractiveBoxProps = {
  children:
    | React.ForwardRefExoticComponent<{
        ref: React.Ref<InteractiveForwardRefProps> | any;
      }>
    | any;
  withPlay?: boolean;
  withReset?: boolean;
  withPause?: boolean;
  withPlayPause?: boolean;
  theme?: 'light' | 'dark';
  style?: StyleProp<ViewStyle>;
};

export const InteractiveBox: React.FC<InteractiveBoxProps> = ({
  children: Children,
  withPlay = false,
  withReset = false,
  withPause = false,
  withPlayPause = false,
  theme = 'light',
  style,
}) => {
  const childRef = useRef<InteractiveForwardRefProps>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);

  const onPlay = useCallback(() => {
    childRef.current?.play();
  }, []);

  const onReset = useCallback(() => {
    childRef.current?.reset();
  }, []);

  const onPause = useCallback(() => {
    childRef.current?.pause();
  }, []);

  const onPlayPause = useCallback(() => {
    if (isPlaying) {
      childRef.current?.pause();
    } else {
      childRef.current?.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const buttons = useMemo(() => {
    const buttonConfigs: ButtonConfig[] = [];
    if (withPlayPause) {
      buttonConfigs.push({
        type: 'play-pause',
        onClick: onPlayPause,
        isPlaying,
      });
    } else {
      if (withPlay) {
        buttonConfigs.push({ type: 'play', onClick: onPlay });
      }
      if (withPause) {
        buttonConfigs.push({ type: 'pause', onClick: onPause });
      }
    }
    if (withReset) {
      buttonConfigs.push({ type: 'reset', onClick: onReset });
    }
    return buttonConfigs;
  }, [
    withPlay,
    withPause,
    withPlayPause,
    withReset,
    onPlay,
    onPause,
    onPlayPause,
    onReset,
    isPlaying,
  ]);

  return (
    <div
      className="min-w-full max-w-full aspect-video mb-4"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: `${INTERACTIVE_CONSTANTS.CONTAINER.BORDER_RADIUS}px`,
        overflow: 'hidden',
        borderWidth: theme === 'light' ? '1px' : '0',
        borderColor:
          INTERACTIVE_CONSTANTS.THEME[
            theme.toUpperCase() as keyof typeof INTERACTIVE_CONSTANTS.THEME
          ].BORDER_COLOR,
        width: 0,
        ...(StyleSheet.flatten(style) as CSSProperties),
      }}
    >
      <View style={{ flex: 1, height: '100%', cursor: 'pointer' }}>
        <Children ref={childRef} />
        <InteractiveButtons buttons={buttons} theme={theme} />
      </View>
    </div>
  );
};

export const withInteractiveBox = (
  Component: (ref: React.Ref<InteractiveForwardRefProps>) => React.ReactNode
) => {
  return forwardRef<InteractiveForwardRefProps>((props, ref) => {
    const children = Component(ref);
    return <InteractiveBox {...props}>{children}</InteractiveBox>;
  });
};
